// BL-1061: the single source of truth for auction + lot status LABELS, TONES,
// and the plain-English DESCRIPTIONS.
//
// LABELS and TONES are live: the status transition menus
// (`lot-status-menu-model` / `auction-status-menu-model`) and the badge builders
// read them. DESCRIPTIONS are NOT — BL-1293 retired the hover legend that used to
// render them, and BL-1416 removed the menus' last read of them. See the field's
// own comment for why they are kept rather than deleted (BL-1301 is their home).
//
// The badge builders import the label/tone maps back from here (AuctionStatusBadge,
// LotStatusBadge) so the pill and its legend can never disagree. Descriptions are
// operator-facing copy (reviewed via the BL-1061 design mockup). Keyed exhaustively
// on the canonical unions (ADR-023 `AuctionPhaseKind` / `LotPhaseKind`) so adding a
// new lifecycle token breaks this file at compile time until its legend row is written.

import type { BadgeTone } from '@auctogon/ui/types';
import type { AuctionPhaseKind, LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';

import type { InvoicePaymentDisplay } from '@auctogon/core/isomorphic/invoices/invoice-page-view';
import type { InvoiceStatus } from '@auctogon/core/isomorphic/invoices/invoice-status';
import type { PaymentRowStatus } from '@auctogon/core/isomorphic/ledger/payment-row';

/** One row in a status legend: the pill's look plus its explanation. */
export interface StatusLegendEntry<K extends string = string> {
  /** The lifecycle token this row describes (highlighted when it's the current one). */
  key: K;
  /** Human label shown on the pill and the legend row. */
  label: string;
  /**
   * One-line plain-English explanation of the phase.
   *
   * ⚠️ **Currently has NO reader.** BL-1293 retired the hover legend, and BL-1416
   * removed the last two reads (the current-row branch in the lot and auction
   * status-menu models) when that row stopped carrying a description. Kept
   * deliberately rather than deleted: **BL-1301** — the in-app status reference
   * page, generated from the transition catalog — is the surface these were
   * written for, and the wording is signed-off copy (the Sale / No Sale
   * distinctions in particular, BL-1380). Deleting ~19 strings now would mean
   * re-authoring them there.
   *
   * Stays REQUIRED on purpose: making it optional would let a new phase ship
   * without the copy BL-1301 needs, which is the opposite of the intent.
   *
   * Declared as consolidation residue on BL-1416 (ADR 044). If BL-1301 is
   * dropped or lands without using these, delete the field.
   */
  description: string;
  /** Semantic tone driving the swatch/badge colour. */
  tone: BadgeTone;
  /** Leading status dot (in-flight states). */
  dot?: boolean;
  /** Pulsing "live right now" dot (auction Active only). Requires `dot`. */
  pulse?: boolean;
  /** Optional visual grouping header (lots: Before sale / Live / After sale / Removed). */
  group?: string;
}

// ── Auction ────────────────────────────────────────────────────────────────
// Meta is exhaustive over AuctionPhaseKind; ORDER is the lifecycle display order.
//
// BL-1292: every auction phase now carries a `group`, the way the lot phases
// always have. The auction status DROPDOWN renders the ladder grouped Before
// sale / Live / After sale / Removed for orientation, and it reads the group
// from here so the two ladders can never be grouped differently.

const AUCTION_PHASE_META: Record<AuctionPhaseKind, Omit<StatusLegendEntry<AuctionPhaseKind>, 'key'>> = {
  // BL-1129 / BL-1285: the KEY is the ADR-023 phase token; the LABEL is
  // operator-facing copy. They deliberately differ — do not "fix" the key to
  // match. (`ready` reads "Upcoming" to operators, and always has.)
  draft: { label: 'Draft', tone: 'neutral', group: 'Before sale', description: 'Still being set up — not shown on the website.' },
  ready: { label: 'Upcoming', tone: 'info', group: 'Before sale', description: 'Visible on the website and scheduled.' },
  // BL-1291: the auction's own pre-bidding phase. Before this rung the auction
  // read "Upcoming" while its lots quietly took pre-bids, so nothing on the
  // list or header told an operator pre-bidding was live. `dot` (no `pulse` —
  // that stays exclusive to `active`) because it IS an in-flight state.
  //
  // BUG-2105 (Dan Saper, 2026-10-15): teal (`brand`), the lot's `pre-bidding`
  // tone. The clerk console shows the two pills side by side, and one word in
  // two colors read as two states. Teal also keeps the rung apart from the
  // blue "Upcoming" one row up, on both ladders.
  'accepting-pre-bids': { label: 'Accepting pre-bids', tone: 'brand', dot: true, group: 'Before sale', description: 'Buyers can leave absentee max bids. Bidding itself has not opened yet.' },
  // BL-1538: "Accepting bids" — sentence case, so it sits beside its neighbour
  // "Accepting pre-bids" and says what an operator can DO with the auction
  // rather than merely that it exists. Label only: the `active` token, every
  // decision stamp and every transition are untouched.
  active: { label: 'Accepting bids', tone: 'success', dot: true, pulse: true, group: 'Live', description: 'Open and running now. Lots close on their own schedules.' },
  // BL-1285: the row the legacy 6-value label could never express. Bidding is
  // halted; the auction stays visible and closable.
  paused: { label: 'Paused', tone: 'warning', dot: true, group: 'Live', description: 'Bidding is temporarily halted. Still visible to buyers; resume to accept bids again.' },
  closed: { label: 'Closed', tone: 'neutral', group: 'After sale', description: 'Formally closed. The auction is done.' },
  // BUG-2105 (Dan Saper, 2026-10-15): gray, not red. The lot's Deleted is gray
  // (BL-1380), and so is every other deleted or archived status in the app.
  // Red is for a problem someone has to act on.
  deleted: { label: 'Deleted', tone: 'neutral', group: 'Removed', description: 'Removed from the system.' },
};

// BL-1291: display order follows the lifecycle, so `accepting-pre-bids` sits
// between Upcoming and Active — the order an operator moves through, and the
// same slot the precedence ladder gives it.
const AUCTION_PHASE_ORDER: readonly AuctionPhaseKind[] = ['draft', 'ready', 'accepting-pre-bids', 'active', 'paused', 'closed', 'deleted'];

/** The auction status legend, in lifecycle order. */
export const AUCTION_STATUS_LEGEND: readonly StatusLegendEntry<AuctionPhaseKind>[] = AUCTION_PHASE_ORDER.map(
  (key) => ({ key, ...AUCTION_PHASE_META[key] }),
);

/**
 * The lifecycle groups a phase can belong to.
 *
 * Deliberately entity-NEUTRAL: the auction ladder and the lot ladder use the
 * same four groups (see `LOT_PHASE_META` below), so the order they display in
 * is one fact, not two.
 */
export type PhaseGroup = 'Before sale' | 'Live' | 'After sale' | 'Removed';

// BL-1411: the ORDER of those groups now has an owner. It previously lived as a
// byte-identical local `SECTION_ORDER` const in BOTH `auction-status-menu-model.ts`
// and `lots/lot-status-menu-model.ts`; the phone auctions list would have been a
// third. All three now read from here, so a group rename or re-sequence is one
// edit rather than three that silently drift (ADR 044).
//
// Two orders, not one, because the two surfaces genuinely order differently and
// that difference is a decision rather than caller freedom (ADR 044's "named
// variant the owner switches on"): a status MENU reads as a lifecycle ladder
// top-to-bottom, while a CLERK's phone list must lead with the sale that is
// running right now.

/** Lifecycle order — a status dropdown's ladder reads top-to-bottom. */
export const PHASE_GROUP_ORDER: readonly PhaseGroup[] = [
  'Before sale',
  'Live',
  'After sale',
  'Removed',
];

/** Clerk-first order — the running sale leads. Used by the phone auctions list. */
export const PHASE_GROUP_ORDER_LIVE_FIRST: readonly PhaseGroup[] = [
  'Live',
  'Before sale',
  'After sale',
  'Removed',
];

/**
 * The lifecycle group a phase belongs to.
 *
 * Reads the group off the legend meta rather than a parallel phase→group map, so
 * a phase that is re-grouped in `AUCTION_PHASE_META` moves on every surface at
 * once. Falls back to 'Removed' only if a phase were ever authored without a
 * group — the type makes that unreachable today.
 */
export function auctionPhaseGroup(phase: AuctionPhaseKind): PhaseGroup {
  return (AUCTION_PHASE_META[phase]?.group as PhaseGroup | undefined) ?? 'Removed';
}

/** Badge tone/label lookup consumed by `AuctionStatusBadge` (single source with the legend). */
export const AUCTION_STATUS_TONE: Record<AuctionPhaseKind, { tone: BadgeTone; dot?: boolean; pulse?: boolean; label: string }> =
  Object.fromEntries(
    AUCTION_PHASE_ORDER.map((key) => [key, { tone: AUCTION_PHASE_META[key].tone, dot: AUCTION_PHASE_META[key].dot, pulse: AUCTION_PHASE_META[key].pulse, label: AUCTION_PHASE_META[key].label }]),
  ) as Record<AuctionPhaseKind, { tone: BadgeTone; dot?: boolean; pulse?: boolean; label: string }>;

// ── Lot / inventory item ─────────────────────────────────────────────────────
// One badge, one ladder (BL-1037 + BL-1061). Meta is exhaustive over LotPhaseKind;
// ORDER groups the 12 phases Before sale / Live / After sale / Removed for display.

const LOT_PHASE_META: Record<LotPhaseKind, Omit<StatusLegendEntry<LotPhaseKind>, 'key'>> = {
  // BL-1129: same key-vs-label split as the auction map above — `ready` is the
  // ADR-023 LotPhaseKind token, "Upcoming" is what an operator reads.
  draft: { label: 'Draft', tone: 'neutral', group: 'Before sale', description: 'Still being set up — not shown on the website.' },
  ready: { label: 'Upcoming', tone: 'info', group: 'Before sale', description: 'Visible on the website when assigned to a visible auction; waiting to open.' },
  // BL-1565: the lot ladder adopts the AUCTION's words on its two open rungs.
  //
  // The BIDDER says the same two words as of the same item
  // (`apps/bidder-web/src/constants/lot-state-labels.ts`), and that is a
  // COINCIDENCE the two owners arrived at — NOT a shared fact, and NOT a rule #18
  // violation. Rule #18 governs shared *logic*; operator copy and buyer copy are
  // separate vocabularies by standing decision (BL-1538; bidder-web has its own
  // Copy rule; this map says "Sale" / "No Sale" where the buyer says otherwise).
  // Sharing two strings would make any future buyer-only re-word a cross-app edit.
  // That file carries the mirror of this note.
  // This REVERSES BL-1538's RAC 3, which pinned the two vocabularies as
  // deliberately separate — requested by Michael Lawrie the same day BL-1538
  // shipped, because the clerk rail renders both ladders at once and an auction
  // reading "Accepting bids" beside its own lots reading "Open for bidding" is
  // the drift PRJ-40 exists to close. The Status Reference page showing one word
  // in both columns on these rungs is the INTENDED outcome, not a bug.
  //
  // Label + tone only: the `pre-bidding` / `open-for-bidding` KEYS are the
  // ADR 023 phase tokens every decision-stamp predicate, transition id, DB value
  // and wire contract uses, and they do not move.
  'pre-bidding': { label: 'Accepting pre-bids', tone: 'brand', group: 'Before sale', description: 'Accepting advance bids before regular bidding opens.' },
  // `pulse` (BL-1565 AC 5): this is the one lot rung that is live RIGHT NOW, so
  // it gets the same flashing dot the auction's `active` rung has carried since
  // BL-966 and the Items tab's "Live" filter chip already uses. `last-call` and
  // `paused` stay on the flat dot — in-flight, not running.
  'open-for-bidding': { label: 'Accepting bids', tone: 'success', dot: true, pulse: true, group: 'Live', description: 'Live now — buyers can bid on this lot.' },
  // BL-1682 (sign-off: Michael Lawrie, 2026-09-11) — the two ZERO-STATE rungs.
  // Both words live here and NOWHERE else. The clerk console's catalog row used
  // to hardcode its own "Closing…" in the time slot while the same lot's status
  // pill rendered this map, so one lot read two ways one column apart. The
  // hardcoded twins are deleted; these entries are the only copy.
  //
  // "Closed" was considered and REJECTED for the auto-close rung: anti-snipe may
  // have extended the lot, so at zero it is genuinely not known to be closed —
  // and asserting so is wrong in exactly the window that matters most. Both keep
  // the `Live` group, the warning tone and the running dot for the same reason.
  //
  // `last-call` drops its capital C, which CLOSES a cross-app split rather than
  // opening one: bidder-web's canonical LAST_CALL_LABEL is already 'Last call!'
  // (BUG-1235), and LotBidPanel asserts the capital-C spelling never appears there.
  'last-call': { label: 'Last call', tone: 'warning', dot: true, group: 'Live', description: 'Past its close, still open for bidding — waiting on manual close by a clerk.' },
  'awaiting-resolution': { label: 'Awaiting result', tone: 'warning', dot: true, group: 'Live', description: 'Past its close and closed to bids — waiting on the server to resolve the outcome.' },
  paused: { label: 'Paused', tone: 'warning', dot: true, group: 'Live', description: 'Bidding is paused temporarily.' },
  // BL-1380: the operator vocabulary for a lot's terminal outcome. "Sale" and
  // "No Sale" are the going-forward words and they live HERE, once, because
  // every surface that renders a lot status reads this map — the clerk
  // console, Items, Lotting, Post-Auction and the lot panel move together or
  // not at all. Tones are load-bearing: green means SOLD, not "finished".
  sold: { label: 'Sale', tone: 'success', group: 'After sale', description: 'Won at the hammer price.' },
  unsold: { label: 'No Sale', tone: 'warning', group: 'After sale', description: 'Closed without selling (no bids, or reserve not met).' },
  // BL-1380 AC 3 (owner ruling, 2026-08-25): Withdrawn is NOT neutral. It is the
  // one terminal outcome a person chose to impose on the lot rather than one the
  // sale arrived at, and on the clerk rail it has to be distinguishable at a
  // glance from `deleted` — which stays neutral precisely so the two do not
  // collapse back into one grey word.
  //
  // BL-1565 AC 6 (owner ruling, 2026-09-04) RE-SIGNED which tone that is. The
  // original ruling said `danger`; with `open-for-bidding` now carrying a live
  // pulsing dot one row up, rose on the clerk rail read as an error sitting
  // beside the sale that is running correctly — and it was the same rose every
  // validation message and destructive confirmation wears. Withdrawn moves to
  // the purple `revoked` tone, which satisfies the 2026-08-25 requirement
  // (still not grey, still not `deleted`) without competing with the live
  // indicator or the error vocabulary. `revoked` is BL-1565's seventh
  // `BadgeTone` and Withdrawn is its only member.
  //
  // Red if this reverts: status-legend.test.ts "AC 2" + the BL-1565 block, and
  // LotCatalogRow.test.tsx "AC 3".
  withdrawn: { label: 'Withdrawn', tone: 'revoked', group: 'Removed', description: 'Pulled from the auction before it sold.' },
  deleted: { label: 'Deleted', tone: 'neutral', group: 'Removed', description: 'Removed from the system.' },
};

/**
 * Every lot phase, in lifecycle order — and the order they RENDER in.
 *
 * BL-1416 kept a second, filtered display order beside this one because `passed`
 * was an unreachable token sharing `unsold`'s "No Sale" label, so rendering it
 * put two identical rows on screen. BL-1387 PR 2 retired the token outright (it
 * is gone from `LotPhaseKind`), so the display order and the canonical order are
 * now the same list — this const is the only one, and every phase renders.
 */
export const LOT_PHASE_ORDER: readonly LotPhaseKind[] = [
  'draft', 'ready', 'pre-bidding',
  'open-for-bidding', 'last-call', 'awaiting-resolution', 'paused',
  'sold', 'unsold',
  'withdrawn', 'deleted',
];

/** The lot/inventory-item status legend, grouped in lifecycle order. */
export const LOT_STATUS_LEGEND: readonly StatusLegendEntry<LotPhaseKind>[] = LOT_PHASE_ORDER.map(
  (key) => ({ key, ...LOT_PHASE_META[key] }),
);

/** Phase → pill label, consumed by `LotStatusBadge` (single source with the legend). */
export const LOT_PHASE_LABELS: Record<LotPhaseKind, string> = Object.fromEntries(
  LOT_PHASE_ORDER.map((key) => [key, LOT_PHASE_META[key].label]),
) as Record<LotPhaseKind, string>;

/**
 * Phase → tone/dot/pulse, consumed by `LotStatusBadge` and the lot status menu
 * model (single source with the legend).
 *
 * BL-1565 added `pulse` to the projection. It is deliberately the SAME shape as
 * `AUCTION_STATUS_TONE`'s: the two ladders' live rungs are one decision, so a
 * projection that carried it on one side and dropped it on the other is how the
 * dropdown lost the dot in the first place.
 */
export const LOT_PHASE_TONE: Record<LotPhaseKind, { tone: BadgeTone; dot?: boolean; pulse?: boolean }> = Object.fromEntries(
  LOT_PHASE_ORDER.map((key) => [key, { tone: LOT_PHASE_META[key].tone, dot: LOT_PHASE_META[key].dot, pulse: LOT_PHASE_META[key].pulse }]),
) as Record<LotPhaseKind, { tone: BadgeTone; dot?: boolean; pulse?: boolean }>;

/**
 * Map a legacy lot/inventory status word (the drained `auction_lot_status`
 * vocabulary + inventory aliases) to the closest ADR-023 `LotPhaseKind`, so the
 * legend can highlight the current row when a caller only holds the legacy
 * `status` and no derived `phaseKind` (rare post-BL-942). Returns `null` for an
 * unknown/absent status (legend renders with nothing highlighted).
 */
export function mapLegacyLotStatusToPhase(status: string | null | undefined): LotPhaseKind | null {
  if (!status) return null;
  const s = status.toLowerCase();
  const direct: Record<string, LotPhaseKind> = {
    draft: 'draft',
    ready: 'ready',
    published: 'ready',
    pre_bidding: 'pre-bidding',
    active: 'open-for-bidding',
    paused: 'paused',
    sold: 'sold',
    // BL-1387 PR 2 retired the `passed` PHASE token, but legacy DATA can still
    // carry the literal string "passed" — it maps onto the surviving `unsold`
    // phase, the same "No Sale" outcome in the operator's vocabulary.
    passed: 'unsold',
    unsold: 'unsold',
    no_sale: 'unsold',
    withdrawn: 'withdrawn',
    deleted: 'deleted',
  };
  return direct[s] ?? null;
}

// ── Invoice ──────────────────────────────────────────────────────────────────
// BL-2035: the one invoice status → label/tone map, read by InvoiceStatusBadge.
// Keyed exhaustively on `InvoiceStatus` (ADR 065 — Money Model § 2), so a new
// status breaks this file at compile time until its pill is written. Labels are
// the signed status matrix's words (docs/feature/admin/invoice-status-matrix.md).
// BL-2424: Past due is orange (`attention`) so Collections (`danger`) reads as the
// step up, and Void is the dashed grey `inert` so it no longer shares Written off's
// purple. Payment arrangement stays `warning` (Dan Saper, 2026-10-09: teal read too
// positive beside Closed's green).

/** Badge label and tone per invoice status. */
export const INVOICE_STATUS_TONE: Record<InvoiceStatus, { tone: BadgeTone; label: string }> = {
  preparing: { tone: 'neutral', label: 'Preparing' },
  open: { tone: 'info', label: 'Open' },
  past_due: { tone: 'attention', label: 'Past due' },
  closed: { tone: 'success', label: 'Closed' },
  payment_arrangement: { tone: 'warning', label: 'Payment arrangement' },
  collections: { tone: 'danger', label: 'Collections' },
  written_off: { tone: 'revoked', label: 'Written off' },
  void: { tone: 'inert', label: 'Void' },
};

/** Badge label and tone per payment display value (ADR 065 § 4, plus "Not sent"). */
export const INVOICE_PAYMENT_TONE: Record<InvoicePaymentDisplay, { tone: BadgeTone; label: string }> = {
  not_sent: { tone: 'neutral', label: 'Not sent' },
  unpaid: { tone: 'neutral', label: 'Unpaid' },
  partially_paid: { tone: 'warning', label: 'Partially paid' },
  paid: { tone: 'success', label: 'Paid' },
  credit_due: { tone: 'info', label: 'Credit due' },
};

// ── Payments row ─────────────────────────────────────────────────────────────
// BL-2382: the one Payments-row status → label/tone map, read by
// PaymentRowStatusBadge. Keyed exhaustively on `PaymentRowStatus`, so a status a
// later item adds (PAY-3a, 5b, 5d, 8) breaks this file until its pill is
// written. Failed (the money never moved) is red; a chargeback and a returned
// refund are amber, as PAY-8's chargeback badge asks; a partial reversal is the
// orange step before the neutral, finished Reversed.

/** Badge label and tone per Payments-row status (labels approved by Michael Lawrie, 2026-10-12). */
export const PAYMENT_ROW_STATUS_TONE: Record<PaymentRowStatus, { tone: BadgeTone; label: string }> = {
  processing: { tone: 'info', label: 'Processing' },
  paid: { tone: 'success', label: 'Paid' },
  partly_reversed: { tone: 'attention', label: 'Partly reversed' },
  reversed: { tone: 'neutral', label: 'Reversed' },
  refunded: { tone: 'neutral', label: 'Refunded' },
  returned: { tone: 'warning', label: 'Returned' },
  chargeback: { tone: 'warning', label: 'Chargeback' },
  failed: { tone: 'danger', label: 'Failed' },
  // BL-2387 AC 5: a reversal the processor hasn't decided yet; it already counts.
  reversal_processing: { tone: 'info', label: 'Reversal processing' },
};
